import { Box, Button, Checkbox, Chip, Container, Paper, Stack, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { MuiTelInput } from 'mui-tel-input';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLoader } from 'src/contexts/LoaderContext';
import feedbackApi from 'src/libs/api/jooicez-backend/spartacus/customer-care/feedback';
import * as Yup from 'yup';

/**
 * Public patient feedback landing page.
 *
 * SECURITY:
 * - The URL contains ONLY an opaque per-org `orgToken` (never the sequential integer
 *   organizationId). Unknown tokens resolve to a generic "Link not found" page.
 * - If a `patientId` query param is provided, the page verifies the patient belongs to
 *   the org resolved from the token. Mismatch falls back to anonymous self-register.
 * - All writes (patient creation, feedback) are bound to the resolved org.
 */

const validationSchema = Yup.object({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  phoneNumber: Yup.string().trim().required('Phone number is required'),
  rating: Yup.number().min(1).max(10).required('Rating is required'),
  notes: Yup.string().trim().nullable(),
  marketingConsent: Yup.boolean(),
  twilioOptIn: Yup.boolean().test('twilioOptIn', 'You must opt in to receive SMS updates', (value) => !!value),
});

export default function PatientFeedbackPage() {
  const { orgToken } = useParams<{ orgToken: string }>();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');

  const [submitted, setSubmitted] = useState(false);
  const { hideLoader } = useLoader();

  useEffect(() => {
    hideLoader();
  }, [hideLoader]);

  // Fast synchronous patient lookup — no async org resolution on page load.
  const { patient, orgName } = useMemo(() => {
    if (patientId) return feedbackApi.lookupPatient(patientId);
    return { patient: null, orgName: null };
  }, [patientId]);

  const formik = useFormik({
    initialValues: {
      firstName: patient?.firstName ?? '',
      lastName: patient?.lastName ?? '',
      phoneNumber: patient?.phoneNumber ?? '',
      rating: 0,
      notes: '',
      marketingConsent: false,
      twilioOptIn: false,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (!orgToken) return;
      try {
        await feedbackApi.submitFeedback(orgToken, {
          patientId: patient?.id ?? null,
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          rating: values.rating,
          notes: values.notes || undefined,
          marketingConsent: values.marketingConsent,
          twilioOptIn: values.twilioOptIn,
        });
        setSubmitted(true);
      } catch {
        toast.error('Failed to submit feedback. Please try again.');
      }
    },
  });

  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Chip label="Thank you!" color="success" sx={{ mb: 2, fontWeight: 700, fontSize: 16, px: 2, py: 1 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Feedback submitted
        </Typography>
        <Typography color="text.secondary">
          Your feedback has been sent to {orgName ?? 'your provider'}. We appreciate you taking the time!
        </Typography>
      </Container>
    );
  }

  const isReadOnly = Boolean(patient);

  return (
    <Container maxWidth="sm" sx={{ py: 6, overflow: 'auto', maxHeight: '100dvh' }}>
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Box>
            {orgName && (
              <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={700}>
                {orgName}
              </Typography>
            )}
            <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
              How was your experience?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your feedback helps us improve. Rate your visit and let us know how we’re doing.
            </Typography>
          </Box>

          <Box component="form" onSubmit={formik.handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label="First name"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                disabled={isReadOnly}
                fullWidth
              />
              <TextField
                label="Last name"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                disabled={isReadOnly}
                fullWidth
              />
              <MuiTelInput
                label="Phone number"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={(value) => formik.setFieldValue('phoneNumber', value)}
                onBlur={formik.handleBlur}
                error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                disabled={isReadOnly}
                fullWidth
              />

              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Overall rating (1–10)
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <Button
                      key={n}
                      variant={formik.values.rating === n ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => formik.setFieldValue('rating', n)}
                      sx={{ minWidth: 36, p: 0.5 }}
                    >
                      {n}
                    </Button>
                  ))}
                </Stack>
                {formik.touched.rating && formik.errors.rating && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.rating}
                  </Typography>
                )}
              </Box>

              <TextField
                label="Additional notes (optional)"
                name="notes"
                multiline
                minRows={3}
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
              />

              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  name="marketingConsent"
                  checked={formik.values.marketingConsent}
                  onChange={formik.handleChange}
                />
                <Typography variant="body2">
                  I agree to receive marketing communications from {orgName ?? 'this provider'}.
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox name="twilioOptIn" checked={formik.values.twilioOptIn} onChange={formik.handleChange} />
                <Typography variant="body2">
                  I consent to receive SMS updates from {orgName ?? 'this provider'}. <strong>(Required)</strong>
                </Typography>
              </Stack>
              {formik.touched.twilioOptIn && formik.errors.twilioOptIn && (
                <Typography variant="caption" color="error">
                  {formik.errors.twilioOptIn}
                </Typography>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={() => formik.handleSubmit()}
                disabled={formik.isSubmitting}
                fullWidth
              >
                {formik.isSubmitting ? 'Submitting…' : 'Submit Feedback'}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
}
